import React, { useState, useEffect } from 'react';
import { APIKeyDocumentation as APIKeyDocumentationType } from '../../types/apiKeys';
import { apiKeyService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

export const APIKeyDocumentation: React.FC = () => {
  const { showToast } = useToast();
  const [docs, setDocs] = useState<APIKeyDocumentationType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocumentation();
  }, []);

  const loadDocumentation = async () => {
    try {
      setLoading(true);
      const response = await apiKeyService.getAPIKeyDocumentation();
      setDocs(response);
    } catch (error) {
      showToast('Failed to load documentation', 'error');
      console.error('Error loading documentation:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!docs) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No documentation available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{docs.title}</h2>
        <p className="text-gray-600 dark:text-gray-300">{docs.description}</p>
      </div>

      {/* Authentication Methods */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Authentication Methods</h3>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Authorization Header</h4>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
              <code className="text-sm font-mono text-gray-800 dark:text-gray-100">
                {docs.headers.Authorization}
              </code>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">X-API-Key Header</h4>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md">
              <code className="text-sm font-mono text-gray-800 dark:text-gray-100">
                {docs.headers['X-API-Key']}
              </code>
            </div>
          </div>
        </div>
      </div>

      {/* Available Endpoints */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Endpoints</h3>
        <div className="space-y-3">
          {Object.entries(docs.endpoints).map(([name, endpoint]) => (
            <div key={name} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                  {name.replace('_', ' ')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">{endpoint}</p>
              </div>
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full">
                Available
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Code Examples */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Code Examples</h3>
        
        <div className="space-y-6">
          {/* JavaScript/Node.js */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">JavaScript/Node.js</h4>
            <div className="bg-gray-900 text-green-400 p-4 rounded-md overflow-x-auto">
              <pre className="text-sm">
{`// Using fetch
const response = await fetch('${docs.endpoints.search}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'ApiKey YOUR_API_KEY_HERE'
  },
  body: JSON.stringify({
    parameter_type: 1,
    search_value: 'example@email.com'
  })
});

const data = await response.json();
console.log(data);`}
              </pre>
            </div>
          </div>

          {/* Python */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Python</h4>
            <div className="bg-gray-900 text-green-400 p-4 rounded-md overflow-x-auto">
              <pre className="text-sm">
{`import requests

headers = {
    'Content-Type': 'application/json',
    'Authorization': 'ApiKey YOUR_API_KEY_HERE'
}

data = {
    'parameter_type': 1,
    'search_value': 'example@email.com'
}

response = requests.post('${docs.endpoints.search}', 
                        headers=headers, 
                        json=data)
result = response.json()
print(result)`}
              </pre>
            </div>
          </div>

          {/* cURL */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">cURL</h4>
            <div className="bg-gray-900 text-green-400 p-4 rounded-md overflow-x-auto">
              <pre className="text-sm">
{`curl -X POST '${docs.endpoints.search}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: ApiKey YOUR_API_KEY_HERE' \\
  -d '{
    "parameter_type": 1,
    "search_value": "example@email.com"
  }'`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Limits & Security */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rate Limits</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-3"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">{docs.rate_limits}</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-3"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">Default: 1,000 requests per hour</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-3"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">Configurable per API key</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Security Best Practices</h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">{docs.security}</span>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">Store API keys in environment variables</span>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">Never commit API keys to version control</span>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3 mt-2"></div>
              <span className="text-sm text-gray-700 dark:text-gray-200">Rotate keys regularly for security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Handling */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Error Handling</h3>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Common Error Codes</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                <span className="text-sm font-medium">401 Unauthorized</span>
                <span className="text-xs text-gray-600 dark:text-gray-300">Invalid or missing API key</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                <span className="text-sm font-medium">429 Too Many Requests</span>
                <span className="text-xs text-gray-600 dark:text-gray-300">Rate limit exceeded</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                <span className="text-sm font-medium">403 Forbidden</span>
                <span className="text-xs text-gray-600 dark:text-gray-300">API key doesn't have permission</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
